/**
 * ปรับภาพให้ตรงข้อกำหนดในเบราว์เซอร์ก่อนอัปโหลด — สัดส่วน · ความกว้าง · ขนาดไฟล์
 *
 * ใช้กับภาพที่ backend ตรวจเข้มและปฏิเสธเมื่อไม่ตรง (เช่นแบนเนอร์หน้าแรก)
 * ถ้าไม่ปรับให้ ผู้ใช้จะรู้ว่าภาพใช้ไม่ได้ตอนกดบันทึก ซึ่งช้าเกินไป
 * และต้องไปหาโปรแกรมแต่งภาพเอง
 *
 * ⚠️ ทุกอย่างที่ปรับต้องบอกผู้ใช้ (notes) — โดยเฉพาะการครอป ที่อาจตัดข้อความในภาพทิ้ง
 *    และการขยายภาพเล็ก ที่ทำให้ภาพไม่คม
 */

export type FitMode = 'crop' | 'pad';

export interface FitTarget {
  /** อัตราส่วนที่ต้องการ เช่น 16/9 */
  ratio: number;
  /** ความกว้างของภาพที่ได้ (px) — ใช้ค่าต่ำสุดที่ backend รับ เพื่อให้ไฟล์เล็กที่สุด */
  width: number;
  /** ขนาดไฟล์สูงสุด (KB) */
  maxKB: number;
}

export interface FitResult {
  file: File;
  width: number;
  height: number;
  sizeKB: number;
  /** สิ่งที่ถูกปรับ — ว่าง = ใช้ไฟล์เดิมได้เลย ไม่ได้แตะอะไร */
  notes: string[];
  /** สัดส่วนเดิมไม่ตรง — หน้าจอใช้ตัดสินว่าจะให้สลับครอป/เติมขอบหรือไม่ */
  ratioOff: boolean;
}

/** ความคลาดของสัดส่วนที่ยอมรับ — เท่ากับที่ backend ใช้ */
const RATIO_TOLERANCE = 0.01;
/** ชนิดไฟล์ที่ backend รับ */
const ACCEPTED_TYPES = ['image/jpeg', 'image/png', 'image/webp'];
/** ลดคุณภาพทีละขั้นจนไฟล์เล็กพอ — ต่ำกว่า 0.55 ภาพเริ่มเป็นบล็อกให้เห็น */
const QUALITY_STEPS = [0.92, 0.86, 0.8, 0.74, 0.68, 0.62, 0.55];
/** ขยายเกินกว่านี้จึงเตือนว่าภาพอาจไม่คม */
const UPSCALE_WARN_RATIO = 0.9;

const px = (n: number) => `${Math.round(n).toLocaleString('th-TH')}px`;
const kb = (bytes: number) => {
  const k = bytes / 1024;
  return k >= 1024 ? `${(k / 1024).toLocaleString('th-TH', { maximumFractionDigits: 1 })} MB` : `${Math.round(k).toLocaleString('th-TH')} KB`;
};

function loadImage(file: File): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => { URL.revokeObjectURL(url); resolve(img); };
    img.onerror = () => { URL.revokeObjectURL(url); reject(new Error('decode')); };
    img.src = url;
  });
}

function toBlob(canvas: HTMLCanvasElement, quality: number): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob((b) => (b ? resolve(b) : reject(new Error('encode'))), 'image/jpeg', quality);
  });
}

/** มีพิกเซลโปร่งใสหรือไม่ — ดูจากภาพย่อ ไม่ต้องอ่านทุกพิกเซลของภาพใหญ่ */
function hasTransparency(img: HTMLImageElement): boolean {
  const c = document.createElement('canvas');
  c.width = 64;
  c.height = 64;
  const ctx = c.getContext('2d');
  if (!ctx) return false;
  ctx.drawImage(img, 0, 0, 64, 64);
  const data = ctx.getImageData(0, 0, 64, 64).data;
  for (let i = 3; i < data.length; i += 4) if (data[i]! < 250) return true;
  return false;
}

export async function fitImage(file: File, target: FitTarget, mode: FitMode): Promise<FitResult> {
  const img = await loadImage(file);
  const w = img.naturalWidth;
  const h = img.naturalHeight;
  const ratioOff = Math.abs(w / h - target.ratio) > RATIO_TOLERANCE;

  /* ตรงทุกข้อแล้ว — ใช้ไฟล์เดิม ไม่บีบอัดซ้ำให้คุณภาพตก */
  if (!ratioOff && w >= target.width && file.size <= target.maxKB * 1024 && ACCEPTED_TYPES.includes(file.type)) {
    return { file, width: w, height: h, sizeKB: Math.round(file.size / 1024), notes: [], ratioOff };
  }

  const outW = target.width;
  const outH = Math.round(target.width / target.ratio);
  const canvas = document.createElement('canvas');
  canvas.width = outW;
  canvas.height = outH;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('canvas');
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = 'high';
  /* JPG ไม่มีความโปร่งใส — พื้นขาวแทนพื้นดำที่ได้ถ้าไม่เติม */
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, outW, outH);

  const notes: string[] = [];
  /** ความกว้างของส่วนภาพต้นฉบับที่ถูกยืดให้เต็มความกว้างที่ได้ — ใช้ตัดสินว่าขยายภาพหรือไม่ */
  let usedSourceWidth = w;

  if (!ratioOff || mode === 'crop') {
    let sx = 0; let sy = 0; let sw = w; let sh = h;
    if (ratioOff && w / h > target.ratio) {
      sw = h * target.ratio;
      sx = (w - sw) / 2;
      notes.push(`ครอปซ้ายและขวาออกข้างละ ${px(sx)} ของภาพต้นฉบับ — ตรวจว่าข้อความในภาพไม่ถูกตัด`);
    } else if (ratioOff) {
      sh = w / target.ratio;
      sy = (h - sh) / 2;
      notes.push(`ครอปบนและล่างออกข้างละ ${px(sy)} ของภาพต้นฉบับ — ตรวจว่าข้อความในภาพไม่ถูกตัด`);
    }
    usedSourceWidth = sw;
    ctx.drawImage(img, sx, sy, sw, sh, 0, 0, outW, outH);
  } else {
    const scale = Math.min(outW / w, outH / h);
    const dw = w * scale;
    const dh = h * scale;
    ctx.drawImage(img, (outW - dw) / 2, (outH - dh) / 2, dw, dh);
    usedSourceWidth = outW / scale;
    notes.push(dw < outW
      ? `เติมขอบขาวซ้ายและขวาข้างละ ${px((outW - dw) / 2)} ให้ได้ 16:9 โดยไม่ตัดภาพ`
      : `เติมขอบขาวบนและล่างข้างละ ${px((outH - dh) / 2)} ให้ได้ 16:9 โดยไม่ตัดภาพ`);
  }

  /* ขยายไม่ถึง 10% ตาแยกไม่ออก — เตือนแล้วกลายเป็นเสียงรบกวนที่ทำให้คนเลิกอ่านคำเตือน */
  if (usedSourceWidth < outW * UPSCALE_WARN_RATIO) {
    notes.push(`ภาพต้นฉบับกว้าง ${px(usedSourceWidth)} ถูกขยายเป็น ${px(outW)} — อาจไม่คม ควรขอไฟล์ที่ใหญ่กว่านี้`);
  }

  if (!ACCEPTED_TYPES.includes(file.type)) notes.push('แปลงไฟล์เป็น JPG');
  if (file.type === 'image/png' || file.type === 'image/webp' || file.type === 'image/gif') {
    if (hasTransparency(img)) notes.push('พื้นโปร่งใสถูกเติมเป็นสีขาว');
  }

  let blob: Blob | null = null;
  for (const q of QUALITY_STEPS) {
    blob = await toBlob(canvas, q);
    if (blob.size <= target.maxKB * 1024) break;
  }
  if (!blob) throw new Error('encode');
  if (blob.size > target.maxKB * 1024) {
    notes.push(`บีบอัดแล้วยังเหลือ ${kb(blob.size)} เกิน ${target.maxKB.toLocaleString('th-TH')} KB — ระบบจะไม่รับ ลองใช้ภาพที่รายละเอียดน้อยกว่านี้`);
  } else if (file.size > target.maxKB * 1024) {
    notes.push(`บีบอัดจาก ${kb(file.size)} เหลือ ${kb(blob.size)}`);
  }

  const name = file.name.replace(/\.[^.]+$/, '') + '.jpg';
  return {
    file: new File([blob], name, { type: 'image/jpeg' }),
    width: outW,
    height: outH,
    sizeKB: Math.round(blob.size / 1024),
    notes,
    ratioOff,
  };
}
